import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  COULEUR_STATUT_ABONNE,
  filtrerAbonnes,
  LIBELLE_FIN,
  LIBELLE_STATUT_ABONNE,
  listerAbonnes,
  motifValide,
  offrirMois,
  resumerAbonnes,
  traduireErreurPremium,
  type Abonne,
  type FiltreAbonnes,
} from '../../api/premium'
import { Compteur } from '../Compteur'
import { dateCourte, fcfa } from '../Format'
import { ListeDeroulante } from '../ListeDeroulante'
import { Modale } from '../Modale'

const messageDe = (e: unknown) =>
  traduireErreurPremium(e instanceof Error ? e.message : String((e as { message?: unknown })?.message ?? e))

/**
 * Abonnés Premium (migration 0062) — LECTURE SEULE.
 *
 * Le cycle de vie est tenu par la base (tâche horaire `premium-cycle`) : demande de paiement la veille
 * de l'échéance, 3 jours de grâce, fin sans frais. La console ne fait que le montrer. Seul geste
 * possible, pour un ADMIN : offrir un mois (motif obligatoire, journalisé avec son auteur).
 */
export function EcranAbonnesPremium({ estAdmin }: { estAdmin: boolean }) {
  const [liste, setListe] = useState<Abonne[]>([])
  const [filtre, setFiltre] = useState<FiltreAbonnes>('en_cours')
  const [erreur, setErreur] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [cible, setCible] = useState<Abonne | null>(null)

  const charger = useCallback(async () => {
    try {
      setListe(await listerAbonnes())
      setErreur(null)
    } catch (e) {
      setErreur(messageDe(e))
    }
  }, [])

  useEffect(() => {
    void charger()
  }, [charger])

  const resume = useMemo(() => resumerAbonnes(liste), [liste])
  const visibles = useMemo(() => filtrerAbonnes(liste, filtre), [liste, filtre])

  return (
    <>
      <div className="stats">
        <div className="stat">
          <div className="label">Abonnés en cours</div>
          <div className="val" style={{ color: 'var(--green)' }}><Compteur valeur={String(resume.actifs)} /></div>
        </div>
        <div className="stat" title="Demande de paiement créée la veille de l'échéance, pas encore réglée">
          <div className="label">Renouvellement à payer</div>
          <div className="val" style={{ color: 'var(--warning)' }}><Compteur valeur={String(resume.aPayer)} /></div>
        </div>
        <div className="stat" title="Échéance passée, impayée : Premium reste actif 3 jours puis s'arrête sans frais">
          <div className="label">En grâce</div>
          <div className="val" style={{ color: 'var(--danger)' }}><Compteur valeur={String(resume.enGrace)} /></div>
        </div>
        <div className="stat">
          <div className="label">Résiliés (encore actifs)</div>
          <div className="val"><Compteur valeur={String(resume.resilies)} /></div>
        </div>
        <div className="stat" title="Somme des prix de période des abonnés qui renouvellent (mois offerts exclus)">
          <div className="label">Revenu mensuel récurrent</div>
          <div className="val"><Compteur valeur={fcfa(resume.mrr)} /></div>
        </div>
      </div>

      {erreur && <p style={{ color: 'var(--danger)', fontSize: 13 }}>{erreur}</p>}
      {info && <div className="dos-ok" style={{ marginBottom: 12 }}>{info}</div>}

      <div className="search-bar">
        <ListeDeroulante
          valeur={filtre}
          surChangement={(v) => setFiltre(v as FiltreAbonnes)}
          options={[
            { valeur: 'en_cours', libelle: 'En cours' },
            { valeur: 'a_payer', libelle: 'Renouvellement à payer / grâce' },
            { valeur: 'resilies', libelle: 'Résiliés' },
            { valeur: 'termines', libelle: 'Terminés' },
            { valeur: 'tous', libelle: 'Tous' },
          ]}
        />
      </div>

      <div className="card">
        <div className="card-header">
          Abonnés Premium ({visibles.length})
          <div className="spacer" style={{ flex: 1 }} />
          <button className="btn btn-outline btn-sm" onClick={() => void charger()}>Actualiser</button>
        </div>
        <div className="card-body">
          <table>
            <thead>
              <tr>
                <th>Client</th>
                <th>Statut</th>
                <th>Depuis</th>
                <th>Échéance</th>
                <th>À payer</th>
                <th>Mois</th>
                <th>Opérateur</th>
                {estAdmin && <th />}
              </tr>
            </thead>
            <tbody>
              {visibles.length === 0 && (
                <tr>
                  <td colSpan={estAdmin ? 8 : 7} style={{ color: 'var(--n400)' }}>Aucun abonné dans cette vue.</td>
                </tr>
              )}
              {visibles.map((a) => (
                <tr key={a.abonnementId}>
                  <td>
                    <b>{a.nom}</b>
                    <div className="dos-note">{a.telephone ?? a.email ?? ''}</div>
                  </td>
                  <td>
                    <span className={`badge badge-${COULEUR_STATUT_ABONNE[a.statut]}`}>{LIBELLE_STATUT_ABONNE[a.statut]}</span>
                    {a.statut === 'termine' && a.finMotif && (
                      <div className="dos-note">
                        {dateCourte(a.finLe)} · {LIBELLE_FIN[a.finMotif] ?? a.finMotif}
                      </div>
                    )}
                    {a.statut === 'grace' && a.graceJusquA && (
                      <div className="dos-note">s'arrête le {dateCourte(a.graceJusquA)} sans paiement</div>
                    )}
                  </td>
                  <td>{dateCourte(a.depuis)}</td>
                  <td>{dateCourte(a.finPeriode)}</td>
                  <td>{a.montantAPayer !== null ? fcfa(a.montantAPayer) : '—'}</td>
                  <td>
                    {a.nbMois}
                    {a.moisOfferts > 0 && <div className="dos-note">dont {a.moisOfferts} offert{a.moisOfferts > 1 ? 's' : ''}</div>}
                  </td>
                  <td>{a.operateur === 'orange_money' ? 'Orange Money' : a.operateur === 'wave' ? 'Wave' : '—'}</td>
                  {estAdmin && (
                    <td>
                      <button className="btn btn-outline btn-sm" onClick={() => { setInfo(null); setCible(a) }}>
                        Offrir un mois
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="dos-note" style={{ marginTop: 12 }}>
        Le renouvellement, la grâce de 3 jours et les fins d'abonnement sont appliqués par la base toutes les heures.
        Cette liste est en lecture seule : aucun abonnement ne se prolonge ni ne se résilie d'ici.
      </p>

      {cible && (
        <OffrirMois
          abonne={cible}
          surFermer={() => setCible(null)}
          surOffert={(fin) => {
            setInfo(`Mois offert à ${cible.nom} : Premium jusqu'au ${dateCourte(fin)}.`)
            setCible(null)
            void charger()
          }}
        />
      )}
    </>
  )
}

function OffrirMois({ abonne, surFermer, surOffert }: { abonne: Abonne; surFermer: () => void; surOffert: (fin: string) => void }) {
  const [motif, setMotif] = useState('')
  const [occupe, setOccupe] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)

  const valider = async () => {
    setOccupe(true)
    setErreur(null)
    try {
      surOffert(await offrirMois(abonne.clientId, motif))
    } catch (e) {
      setErreur(messageDe(e))
    } finally {
      setOccupe(false)
    }
  }

  return (
    <Modale titre={`Offrir un mois à ${abonne.nom}`} ouverte surFermer={surFermer}>
      <p className="dos-note">
        {abonne.statut === 'termine'
          ? "Le client n'est plus abonné : un mois de Premium lui est ouvert à partir d'aujourd'hui, SANS renouvellement automatique (il peut l'activer depuis l'app)."
          : `L'échéance passe du ${dateCourte(abonne.finPeriode)} à un mois plus tard. Une demande de paiement en cours pour cette échéance est annulée.`}
        {' '}Geste commercial journalisé avec votre nom et le motif.
      </p>
      <label>Motif (obligatoire)</label>
      <input value={motif} placeholder="ex : retard de livraison du 08/05" onChange={(e) => setMotif(e.target.value)} />
      {erreur && <p style={{ color: 'var(--danger)', fontSize: 13 }}>{erreur}</p>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
        <button className="btn btn-outline" onClick={surFermer}>Annuler</button>
        <button className="btn btn-primary" disabled={occupe || !motifValide(motif)} onClick={() => void valider()}>
          {occupe ? 'Envoi…' : 'Offrir un mois'}
        </button>
      </div>
    </Modale>
  )
}
